import Microgame from '../engine/Microgame.js';

export default class AlignGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Alignement !`, en: `Align!`, es: `¡Alinea!` },
            controls: { fr: `Appuie au bon moment`, en: `Tap at the right time`, es: `Toca en el momento exacto` },
            objective: { fr: `Aligne parfaitement les cercles concentriques`, en: `Align the concentric circles perfectly`, es: `Alinea los círculos concéntricos perfectamente` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/></svg>`,
            actions: [
                { type: 'swipe', fromX: 50, fromY: 28, toX: 72, toY: 50, duration: 240 },
                { type: 'swipe', fromX: 72, fromY: 50, toX: 50, toY: 72, duration: 240 },
                { type: 'swipe', fromX: 50, fromY: 72, toX: 28, toY: 50, duration: 240 },
                { type: 'swipe', fromX: 28, fromY: 50, toX: 50, toY: 28, duration: 240 },
                { type: 'swipe', fromX: 50, fromY: 28, toX: 72, toY: 50, duration: 240 },
                { type: 'wait', duration: 380 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.angle = 0;
        this.targetAngle = 0;
        this.isDragging = false;
        this.lastAngle = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "ALIGN!";

        // Random target angle
        this.targetAngle = Math.random() * 360;
        // Start position is somewhat opposite
        this.angle = (this.targetAngle + 180 + (Math.random() * 90 - 45)) % 360;

        // Outer Ring (Static Target)
        this.targetRing = document.createElement('div');
        this.targetRing.style.position = 'absolute';
        this.targetRing.style.top = '50%';
        this.targetRing.style.left = '50%';
        this.targetRing.style.width = '200px';
        this.targetRing.style.height = '200px';
        this.targetRing.style.borderRadius = '50%';
        this.targetRing.style.border = '10px solid rgba(255, 255, 255, 0.2)';
        this.targetRing.style.borderTopColor = this.secondaryColor; // Marker
        this.targetRing.style.transform = `translate(-50%, -50%) rotate(${this.targetAngle}deg)`;
        this.container.appendChild(this.targetRing);

        // Inner Ring (Player Controlled)
        // The circle itself is white/transparent, and the MARKER (borderTopColor) is Primary Color.
        this.playerRing = document.createElement('div');
        this.playerRing.style.position = 'absolute';
        this.playerRing.style.top = '50%';
        this.playerRing.style.left = '50%';
        this.playerRing.style.width = '160px';
        this.playerRing.style.height = '160px';
        this.playerRing.style.borderRadius = '50%';
        this.playerRing.style.border = '10px solid rgba(255, 255, 255, 0.2)';
        this.playerRing.style.borderTopColor = this.primaryColor; // Marker
        this.playerRing.style.transform = `translate(-50%, -50%) rotate(${this.angle}deg)`;
        this.playerRing.style.cursor = 'grab';
        this.playerRing.style.transition = 'box-shadow 0.2s, border-color 0.2s';
        this.container.appendChild(this.playerRing);

        // Interaction
        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        // Difficulty Tolerance
        const sm = this.engine.speedMultiplier || 1.0;
        this.tolerance = Math.max(8, 15 - (sm - 1) * 2);

        this.engine.ui.instruction.textContent = `ALIGN!`;

        // We attach to window input normally, handled via engine
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    getAngle(x, y) {
        const rect = this.container.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        return Math.atan2(y - cy, x - cx) * 180 / Math.PI;
    }

    onDown(e) {
        this.isDragging = true;
        this.lastAngle = this.getAngle(e.clientX, e.clientY);
    }

    onMove(e) {
        if (!this.isDragging || !this.active) return;

        let clientX = e.clientX;
        let clientY = e.clientY;
        if (e.touches && e.touches.length > 0) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        }

        const currentAngle = this.getAngle(clientX, clientY);
        // Fix angle wrapping issues when crossing -180/180
        let delta = currentAngle - this.lastAngle;
        if (delta > 180) delta -= 360;
        if (delta < -180) delta += 360;

        this.angle += delta;
        this.lastAngle = currentAngle;

        this.playerRing.style.transform = `translate(-50%, -50%) rotate(${this.angle}deg)`;
    }

    onUp() {
        this.isDragging = false;
    }

    update(dt) {
        if (!this.active) return;

        // Challenge/Infinite Drift
        if (this.engine.activeChallenge && this.engine.activeChallenge.mode === 'infinite') {
            const sm = this.engine.speedMultiplier || 1.0;
            const driftSpeed = 10 * sm; // Degrees per second

            const time = Date.now() / 1000;
            const drift = Math.sin(time) * driftSpeed * (dt / 1000);

            this.targetAngle += drift;
            this.targetRing.style.transform = `translate(-50%, -50%) rotate(${this.targetAngle}deg)`;
        }

        // Check alignment continuously every frame
        const diff = Math.abs((this.angle % 360) - (this.targetAngle % 360));
        let normalizedDiff = diff % 360;
        if (normalizedDiff > 180) normalizedDiff = 360 - normalizedDiff;

        if (normalizedDiff < this.tolerance && this.active) {
            this.active = false; // Instant win - stop further checks

            // FREEZE the timer immediately — animation time doesn't count
            this.engine.state = 'TRANSITION';

            // Fill animation: inner ring border → primaryColor, outer ring border → secondaryColor
            this.playerRing.style.transition = 'all 0.15s ease-out';
            this.targetRing.style.transition = 'all 0.15s ease-out';

            this.playerRing.style.borderColor = this.primaryColor;
            this.playerRing.style.borderTopColor = this.primaryColor;
            this.playerRing.style.boxShadow = `0 0 20px ${this.primaryColor}`;

            this.targetRing.style.borderColor = this.secondaryColor;
            this.targetRing.style.boxShadow = `0 0 20px ${this.secondaryColor}`;

            // Win after the quick animation (timer is already frozen)
            setTimeout(() => {
                // Restore state and active so win() / handleMicrogameResult works properly
                this.active = true;
                this.engine.state = 'PLAYING';
                this.win();
            }, 150);
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);

        // Restore timer visibility if hidden
        if (this.engine.ui.timerFill) {
            this.engine.ui.timerFill.parentElement.style.opacity = '1';
        }
    }
}
